import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_MS = 150
const POLL_MS = 3000
const PATIENCE_MS = 90000

export default class extends Controller {
  static targets = ["input", "suggestions", "option", "picks", "selected", "parts", "part", "findAll"]
  static values = {
    searchId: Number, styleId: String, resolveUrl: String, picksUrl: String,
    suggestUrl: String, statusUrl: String, packageUrl: String, resolving: String, unresolved: String, retry: String
  }

  connect() {
    if (this.element.parentElement !== document.body) {
      document.querySelectorAll(`#${this.element.id}`).forEach((other) => { if (other !== this.element) other.remove() })
      document.body.append(this.element)
      return
    }

    this.state = this.restore()
    this.loaded = false
    this.warmed = {}
    this.onShortcut = this.shortcut.bind(this)
    this.onShown = this.shown.bind(this)
    this.onHidden = this.hidden.bind(this)
    document.addEventListener("keydown", this.onShortcut)
    this.element.addEventListener("shown.bs.modal", this.onShown)
    this.element.addEventListener("hidden.bs.modal", this.onHidden)
    if (!/Mac|iPhone|iPad/.test(navigator.platform)) {
      document.querySelectorAll("[data-find-parts-shortcut]").forEach((key) => { key.textContent = "Ctrl K" })
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onShortcut)
    this.element.removeEventListener("shown.bs.modal", this.onShown)
    this.element.removeEventListener("hidden.bs.modal", this.onHidden)
    clearTimeout(this.pollTimer)
  }

  shortcut(event) {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return

    event.preventDefault()
    if (this.element.classList.contains("show")) {
      this.focusInput()
    } else {
      bootstrap.Modal.getOrCreateInstance(this.element).show()
    }
  }

  shown() {
    this.focusInput()
    if (!this.loaded) {
      this.loaded = true
      this.loadPicks()
      if (this.state.codes.length) {
        this.selectedTarget.hidden = false
        this.state.codes.forEach((code) => this.partsTarget.append(this.placeholder(code)))
        this.resolve(this.state.codes)
      }
    }
    this.warm()
  }

  hidden() {
    clearTimeout(this.pollTimer)
    this.closeSuggestions()
  }

  type() {
    clearTimeout(this.debounce)
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.closeSuggestions()
      return
    }
    this.debounce = setTimeout(() => this.suggest(query), DEBOUNCE_MS)
  }

  async suggest(query) {
    this.suggestRequest?.abort()
    this.suggestRequest = new AbortController()
    try {
      const html = await this.get(this.suggestUrlValue, { q: query, codes: this.state.codes }, this.suggestRequest.signal)
      this.suggestionsTarget.innerHTML = html
      this.suggestionsTarget.hidden = false
      this.picksTarget.hidden = true
      this.inputTarget.setAttribute("aria-expanded", "true")
      this.highlight(0)
    } catch (error) {
      if (error.name !== "AbortError") this.closeSuggestions()
    }
  }

  key(event) {
    const open = !this.suggestionsTarget.hidden && this.optionTargets.length
    if (event.key === "ArrowDown" && open) {
      event.preventDefault()
      this.highlight(Math.min(this.highlighted + 1, this.optionTargets.length - 1))
    } else if (event.key === "ArrowUp" && open) {
      event.preventDefault()
      this.highlight(Math.max(this.highlighted - 1, 0))
    } else if (event.key === "Enter" || (event.key === "Tab" && open)) {
      const query = this.inputTarget.value.trim()
      if (open) {
        event.preventDefault()
        this.add(this.optionTargets[this.highlighted].dataset.code)
      } else if (/^\d+$/.test(query)) {
        event.preventDefault()
        this.add(query)
      }
    } else if (event.key === "Escape" && !this.suggestionsTarget.hidden) {
      event.preventDefault()
      event.stopPropagation()
      this.closeSuggestions()
    }
  }

  highlight(index) {
    this.highlighted = index
    this.optionTargets.forEach((option, position) => option.setAttribute("aria-selected", position === index))
    const option = this.optionTargets[index]
    if (!option) return

    option.scrollIntoView({ block: "nearest" })
    this.inputTarget.setAttribute("aria-activedescendant", option.id)
  }

  closeSuggestions() {
    clearTimeout(this.debounce)
    this.suggestRequest?.abort()
    this.suggestionsTarget.hidden = true
    this.suggestionsTarget.innerHTML = ""
    this.picksTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  choose(event) {
    this.add(event.currentTarget.dataset.code)
  }

  add(code) {
    this.inputTarget.value = ""
    this.closeSuggestions()
    this.focusInput()
    if (this.state.codes.includes(code)) return

    this.state.codes.unshift(code)
    this.save()
    this.partsTarget.prepend(this.placeholder(code))
    this.selectedTarget.hidden = false
    this.resolve([code])
    this.loadPicks()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-code]")
    const code = row.dataset.code
    this.state.codes = this.state.codes.filter((selected) => selected !== code)
    this.piecesOf(row).forEach((piece) => delete this.state.fits[piece.id])
    delete this.state.chosen[code]
    row.remove()
    this.selectedTarget.hidden = this.state.codes.length === 0
    this.ready()
    this.save()
    this.loadPicks()
    this.focusInput()
  }

  retry(event) {
    this.resolve([event.currentTarget.closest("[data-code]").dataset.code])
  }

  change(event) {
    const row = event.currentTarget.closest("[data-code]")
    delete this.state.chosen[row.dataset.code]
    this.piecesOf(row).forEach((piece) => delete this.state.fits[piece.id])
    this.show(row, false)
  }

  choosePieces(event) {
    const row = event.target.closest("[data-code]")
    const pieces = this.piecesOf(row)
    const chosen = event.target.value === "*" ? pieces.map((piece) => piece.id) : [event.target.value]
    this.state.chosen[row.dataset.code] = chosen
    if (pieces.every((piece) => piece.side)) {
      const sides = new Set(pieces.filter((piece) => chosen.includes(piece.id)).map((piece) => piece.side))
      this.state.side = sides.size === 1 ? [...sides][0] : null
    }
    this.show(row)
    this.focusInput()
  }

  pickFit(event) {
    this.state.fits[event.target.closest("[data-for-slot]").dataset.forSlot] = event.target.value
    this.show(event.target.closest("[data-code]"))
    this.focusInput()
  }

  answer(event) {
    const { key, value } = event.target.dataset
    this.resolve(this.state.codes, { [key]: value })
    this.focusInput()
  }

  async resolve(codes, choices = {}) {
    const body = new FormData()
    codes.forEach((code) => body.append("codes[]", code))
    Object.entries(choices).forEach(([key, value]) => body.append(`choices[${key}]`, value))
    if (this.styleIdValue) body.append("style_id", this.styleIdValue)
    try {
      const response = await fetch(this.resolveUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content, Accept: "text/html" }
      })
      if (!response.ok) throw new Error(response.status)
      this.place(await response.text())
    } catch (error) {
      this.fail(codes)
    }
  }

  fail(codes) {
    codes.map((code) => this.rowFor(code)).filter((row) => row && !row.dataset.pieces).forEach((row) => {
      row.dataset.pieces = "[]"
      row.innerHTML = `<div class="find-parts__row"><span class="find-parts__summary"></span></div>`
      const summary = row.querySelector(".find-parts__summary")
      const retry = document.createElement("button")
      retry.type = "button"
      retry.className = "btn btn-link btn-sm p-0 align-baseline"
      retry.dataset.action = "find-parts#retry"
      retry.textContent = this.retryValue
      summary.append(`${this.unresolvedValue} `, retry)
    })
  }

  place(html) {
    const template = document.createElement("template")
    template.innerHTML = html
    template.content.querySelectorAll("[data-code]").forEach((row) => {
      if (!this.state.codes.includes(row.dataset.code)) return

      const existing = this.rowFor(row.dataset.code)
      if (existing) {
        existing.replaceWith(row)
      } else {
        this.partsTarget.append(row)
      }
      this.show(row)
    })
    this.state.codes.forEach((code) => {
      const row = this.rowFor(code)
      if (row) this.partsTarget.append(row)
    })
  }

  show(row, carry = true) {
    const code = row.dataset.code
    const pieces = this.piecesOf(row)
    if (!this.state.chosen[code] && pieces.length === 1) this.state.chosen[code] = [pieces[0].id]
    if (!this.state.chosen[code] && carry && this.state.side && pieces.length && pieces.every((piece) => piece.side)) {
      const carried = pieces.filter((piece) => piece.side === this.state.side).map((piece) => piece.id)
      if (carried.length) this.state.chosen[code] = carried
    }
    const chosen = (this.state.chosen[code] || []).filter((id) => pieces.some((piece) => piece.id === id))
    const question = pieces.length && !chosen.length
      ? row.querySelector("[data-question=pieces]")
      : chosen.map((id) => row.querySelector(`[data-for-slot="${CSS.escape(id)}"]`)).find((block) => block && !this.state.fits[block.dataset.forSlot])

    row.querySelectorAll("[data-question]").forEach((block) => {
      block.hidden = block !== question
      block.querySelectorAll("input").forEach((input) => { input.checked = false })
    })

    const done = pieces.length > 0 && !question
    if (pieces.length) {
      row.querySelector("[data-summary]").textContent = done && pieces.length > 1
        ? pieces.filter((piece) => chosen.includes(piece.id)).map((piece) => piece.title).join(", ")
        : ""
    }
    row.querySelector("[data-change]").hidden = !(done && pieces.length > 1)
    const view = row.querySelector("[data-view]")
    view.hidden = !done
    if (done) {
      const params = new URLSearchParams({ slot: chosen[0] })
      if (this.state.fits[chosen[0]]) params.append("number", this.state.fits[chosen[0]])
      view.href = `${row.dataset.href}&${params}`
    }
    this.ready()
    this.save()
    this.warm()
  }

  async warm() {
    clearTimeout(this.pollTimer)
    if (!this.element.classList.contains("show")) return

    const params = new URLSearchParams()
    this.partTargets.forEach((row) => {
      const pieces = this.piecesOf(row)
      ;(this.state.chosen[row.dataset.code] || []).forEach((id) => {
        const number = this.state.fits[id] || pieces.find((piece) => piece.id === id)?.number
        if (!number || this.warmed[number] === "done") return

        this.warmed[number] ||= Date.now()
        if (Date.now() - this.warmed[number] < PATIENCE_MS) params.append(`pieces[${id}]`, number)
      })
    })
    if (![...params.keys()].length) return

    try {
      const states = JSON.parse(await this.get(`${this.statusUrlValue}?${params}`))
      Object.entries(states).forEach(([number, status]) => {
        if (status.state !== "fetching") this.warmed[number] = "done"
      })
    } catch (error) {
      return
    }
    this.pollTimer = setTimeout(() => this.warm(), POLL_MS)
  }

  ready() {
    this.findAllTarget.disabled = !this.partTargets.some((row) => (this.state.chosen[row.dataset.code] || []).length)
  }

  findAll() {
    if (this.partTargets.some((row) => !row.dataset.pieces)) return

    const ask = this.partTargets.map((row) => row.querySelector("[data-question]:not([hidden]) input, [data-action='find-parts#retry']")).find(Boolean)
    if (ask) {
      ask.scrollIntoView({ block: "nearest" })
      ask.focus()
      return
    }

    const chosenRows = this.partTargets.filter((row) => (this.state.chosen[row.dataset.code] || []).length)
    if (chosenRows.length === 1) {
      window.location.assign(chosenRows[0].querySelector("[data-view]").href)
      return
    }

    const params = new URLSearchParams()
    this.partTargets.forEach((row) => {
      const pieces = this.piecesOf(row)
      ;(this.state.chosen[row.dataset.code] || []).forEach((id) => {
        const piece = pieces.find((candidate) => candidate.id === id)
        if (piece) params.append(`pieces[${id}]`, this.state.fits[id] || piece.number || "")
      })
    })
    if (this.styleIdValue) params.append("style_id", this.styleIdValue)
    window.location.assign(`${this.packageUrlValue}?${params}`)
  }

  placeholder(code) {
    const row = document.createElement("li")
    row.className = "find-parts__part"
    row.dataset.code = code
    row.dataset.findPartsTarget = "part"
    row.innerHTML = `<div class="find-parts__row"><span class="spinner-border spinner-border-sm text-secondary" role="status"></span><span class="find-parts__summary"></span></div>`
    row.querySelector(".find-parts__summary").textContent = this.resolvingValue
    return row
  }

  async loadPicks() {
    try {
      this.picksTarget.innerHTML = await this.get(this.picksUrlValue, { codes: this.state.codes })
    } catch (error) {
      this.picksTarget.innerHTML = ""
    }
  }

  async get(url, params = {}, signal) {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) value.forEach((item) => query.append(`${key}[]`, item))
      else query.append(key, value)
    })
    if (this.styleIdValue) query.append("style_id", this.styleIdValue)
    const separator = url.includes("?") ? "&" : "?"
    const response = await fetch(`${url}${separator}${query}`, { signal, headers: { "X-Requested-With": "XMLHttpRequest" } })
    if (!response.ok) throw new Error(response.status)
    return response.text()
  }

  piecesOf(row) {
    return JSON.parse(row.dataset.pieces || "[]")
  }

  rowFor(code) {
    return this.partTargets.find((row) => row.dataset.code === code)
  }

  focusInput() {
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  storageKey() {
    return `find-parts-${this.searchIdValue}`
  }

  restore() {
    const blank = { codes: [], chosen: {}, fits: {}, side: null }
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.storageKey()) || "{}")
      return { ...blank, ...stored, chosen: stored.chosen || {}, fits: stored.fits || {} }
    } catch (error) {
      return blank
    }
  }

  save() {
    try {
      sessionStorage.setItem(this.storageKey(), JSON.stringify(this.state))
    } catch (error) {
      return
    }
  }
}
